import React, { useRef } from "react";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";
import "./RelatorioEntradaMaterialPrint.css";

const COLUNAS = [
  ["codigo", "Código", "8%"],
  ["data", "Data", "9%"],
  ["cliente", "Cliente", "15%"],
  ["obra", "Obra", "16%"],
  ["fornecedor", "Fornecedor", "13%"],
  ["localColeta", "Local de coleta", "11%"],
  ["descricao", "Descrição", "19%"],
  ["status", "Status", "9%"],
];

const ID_QUADRO_IMPRESSAO = "quadro-impressao-entrada-material";

const ESTILOS_PDF_ENTRADA_MATERIAL = `
  @page { size: A4 landscape; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #071b31; font-family: Arial, sans-serif; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .pagina-pdf-entrada-material { width: 297mm; height: 210mm; margin: 0; padding: 15mm 15mm 6px; overflow: hidden; break-after: page; page-break-after: always; }
  .pagina-pdf-entrada-material:last-child { break-after: auto; page-break-after: auto; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .cabecalho-relatorio-entrada-material td { border: 1px solid #153d64; padding: 5px; text-align: center; }
  .cabecalho-relatorio-entrada-material td:first-child { width: 28mm; }
  .cabecalho-relatorio-entrada-material td:last-child { width: 42mm; }
  .cabecalho-relatorio-entrada-material img { max-width: 26mm; max-height: 15mm; }
  .cabecalho-relatorio-entrada-material strong, .cabecalho-relatorio-entrada-material span { display: block; }
  .cabecalho-relatorio-entrada-material td:nth-child(2) strong { font-size: 16px; color: #0b4b78; }
  .cabecalho-relatorio-entrada-material span { margin-top: 3px; font-size: 9px; }
  .tabela-relatorio-entrada-material { font-size: 8px; }
  .tabela-relatorio-entrada-material th, .tabela-relatorio-entrada-material td { border: 1px solid #557a4c; padding: 4px 3px; text-align: center; vertical-align: middle; overflow-wrap: anywhere; }
  .tabela-relatorio-entrada-material th { background: #0b4774; color: #fff; font-size: 8.2px; }
  .tabela-relatorio-entrada-material .linha-cabecalho-documento > th { border: 0; padding: 0 0 4mm; background: #fff; color: #071b31; }
  .tabela-relatorio-entrada-material .cabecalho-relatorio-entrada-material td { border: 1px solid #153d64; padding: 5px; }
  .tabela-relatorio-entrada-material tbody tr:nth-child(even) { background: #f4f7f2; }
  .tabela-relatorio-entrada-material tr { break-inside: avoid; page-break-inside: avoid; }
`;

function formatarData(valor) {
  if (!valor) return "-";
  const data = typeof valor.toDate === "function"
    ? valor.toDate()
    : valor.seconds
      ? new Date(valor.seconds * 1000)
      : new Date(valor);
  return Number.isNaN(data.getTime()) ? "-" : data.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

function textoStatus(status) {
  return { nao_iniciado: "Não iniciado", em_andamento: "Em andamento", concluido: "Concluído" }[status] || status || "Não iniciado";
}

function imprimirPdfEntradaMaterial(elemento, nomeArquivo) {
  if (!elemento) return;
  const tabela = elemento.querySelector(".tabela-relatorio-entrada-material");
  const cabecalhoTabela = tabela?.querySelector("thead")?.outerHTML || "";
  const definicaoColunas = Array.from(tabela?.children || []).find((filho) => filho.tagName === "COLGROUP")?.outerHTML || "";
  const corpoTabela = Array.from(tabela?.children || []).find((filho) => filho.tagName === "TBODY");
  const linhas = Array.from(corpoTabela?.children || []);
  if (!tabela || !cabecalhoTabela || linhas.length === 0) return;

  document.getElementById(ID_QUADRO_IMPRESSAO)?.remove();
  const quadroImpressao = document.createElement("iframe");
  quadroImpressao.id = ID_QUADRO_IMPRESSAO;
  quadroImpressao.title = "Impressão da Entrada de Material";
  Object.assign(quadroImpressao.style, { position: "fixed", right: "0", bottom: "0", width: "1px", height: "1px", border: "0", opacity: "0", pointerEvents: "none" });
  document.body.appendChild(quadroImpressao);

  const janelaImpressao = quadroImpressao.contentWindow;
  const documentoImpressao = quadroImpressao.contentDocument;
  documentoImpressao.open();
  documentoImpressao.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${nomeArquivo}</title><style>${ESTILOS_PDF_ENTRADA_MATERIAL}</style></head><body></body></html>`);
  documentoImpressao.close();

  function criarPagina() {
    const pagina = documentoImpressao.createElement("section");
    pagina.className = "pagina-pdf-entrada-material";
    pagina.innerHTML = `<table class="tabela-relatorio-entrada-material">${definicaoColunas}${cabecalhoTabela}<tbody></tbody></table>`;
    documentoImpressao.body.appendChild(pagina);
    return pagina;
  }

  let paginaAtual = criarPagina();
  const imagensCabecalho = Array.from(paginaAtual.querySelectorAll("img"));
  Promise.all(imagensCabecalho.map((imagem) => imagem.complete ? Promise.resolve() : new Promise((resolve) => {
    imagem.addEventListener("load", resolve, { once: true });
    imagem.addEventListener("error", resolve, { once: true });
  }))).then(() => {
    linhas.forEach((linha) => {
      let corpoPagina = Array.from(paginaAtual.querySelector(".tabela-relatorio-entrada-material").children).find((filho) => filho.tagName === "TBODY");
      corpoPagina.insertAdjacentHTML("beforeend", linha.outerHTML);
      const linhaInserida = corpoPagina.lastElementChild;
      const limiteInferior = paginaAtual.getBoundingClientRect().bottom - 6;

      if (linhaInserida.getBoundingClientRect().bottom > limiteInferior && corpoPagina.children.length > 1) {
        linhaInserida.remove();
        paginaAtual = criarPagina();
        corpoPagina = Array.from(paginaAtual.querySelector(".tabela-relatorio-entrada-material").children).find((filho) => filho.tagName === "TBODY");
        corpoPagina.insertAdjacentHTML("beforeend", linha.outerHTML);
      }
    });

    janelaImpressao.onafterprint = () => quadroImpressao.remove();
    janelaImpressao.focus();
    janelaImpressao.print();
  });
}

export default function RelatorioEntradaMaterialPrint({ materiais, mapaObras, filtroPesquisa, filtroStatus, onVoltar }) {
  const nomeArquivo = "RQ-4.7-ENTRADA-DE-MATERIAL";
  const paginaRef = useRef(null);
  useNomeArquivoPdf(nomeArquivo);
  const emitidoEm = new Date().toLocaleDateString("pt-BR");
  const descricaoFiltro = [
    filtroPesquisa ? `Pesquisa: ${filtroPesquisa}` : "",
    filtroStatus && filtroStatus !== "todos" ? `Status: ${textoStatus(filtroStatus)}` : "",
  ].filter(Boolean).join(" | ") || "Todos os registros";

  return (
    <div className="relatorio-entrada-material-print">
      <div className="acoes-relatorio-entrada-material">
        <button type="button" onClick={onVoltar}>Voltar</button>
        <button type="button" className="botao-pdf-entrada-material" onClick={() => imprimirPdfEntradaMaterial(paginaRef.current, nomeArquivo)}>Salvar como PDF</button>
      </div>
      <main ref={paginaRef} className="pagina-relatorio-entrada-material">
        <table className="tabela-relatorio-entrada-material">
          <colgroup>{COLUNAS.map(([campo, titulo, largura]) => <col key={`${campo}-${titulo}`} style={{ width: largura }} />)}</colgroup>
          <thead>
            <tr className="linha-cabecalho-documento">
              <th colSpan={COLUNAS.length}>
                <table className="cabecalho-relatorio-entrada-material">
                  <tbody><tr><td><LogoRelatorio alt="Logo da empresa" /></td><td><strong>ENTRADA DE MATERIAL</strong><span>Sistema de Gestão da Qualidade</span><span>{descricaoFiltro}</span></td><td><strong>RQ 4.7</strong><span>{materiais.length} registro(s)</span><span>Emitido em {emitidoEm}</span></td></tr></tbody>
                </table>
              </th>
            </tr>
            <tr>{COLUNAS.map(([campo, titulo]) => <th key={campo}>{titulo}</th>)}</tr>
          </thead>
          <tbody>
            {materiais.length === 0
              ? <tr><td colSpan={COLUNAS.length}>Nenhum registro encontrado para os filtros informados.</td></tr>
              : materiais.map((material) => {
                  const dados = {
                    codigo: material.codigo || "-",
                    data: formatarData(material.DataCadastro),
                    cliente: material.Cliente || "-",
                    obra: mapaObras[material.ObraId] || "-",
                    fornecedor: material.Fornecedor || "-",
                    localColeta: material.LocalColeta || "-",
                    descricao: material.Descricao || "-",
                    status: textoStatus(material.Status),
                  };
                  return <tr key={material.id || material.codigo}>{COLUNAS.map(([campo]) => <td key={campo}>{dados[campo]}</td>)}</tr>;
                })}
          </tbody>
        </table>
      </main>
    </div>
  );
}
